import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import PageTitle from 'Components/PageTitle'
import { WithLoader } from 'Components/WithLoader'
import {
  useGetCustomerGroupParameterGroupQuery,
  useGetProductGroupParameterGroupQuery
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import ProductDrawer from 'Services/ProductManagement/Features/Product/Components/ProductDrawer'
import { ProductList } from 'Services/ProductManagement/Features/Product/Components/ProductList'
import {
  useProductsWithParams,
  useSingleProduct
} from 'Services/ProductManagement/Features/Product/Hooks'
import {
  PRODUCT_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Tab } from '@mui/material'
import Box from '@mui/material/Box'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

const TAB_PARAM_KEY = 'tab'

function ProductListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/products`

  const navigate = useNavigate()
  const {
    products,
    loading,
    params,
    setParams,
    total_results,
    selectedProductId,
    setSelectedProductId,
    fetchProductsWithParams
  } = useProductsWithParams({
    hasNoTariff: true
  })

  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromParams = searchParams.get(TAB_PARAM_KEY) ?? ''

  const { deleteProduct } = useSingleProduct(undefined)
  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedProductId)

  const [searchText, setSearchText] = useState(params.search_text ?? '')
  const {
    data: productGroupParameterGroup = null,
    refetch: fetchProductGroups,
    isLoading: isProductGroupParameterGroupLoading
  } = useGetProductGroupParameterGroupQuery(undefined)
  const {
    data: customerGroupParameterGroup = null,
    refetch: fetchCustomerGroups,
    isLoading: isCustomerGroupParameterGroupLoading
  } = useGetCustomerGroupParameterGroupQuery(undefined)

  useEffect(() => {
    fetchProductGroups()
    fetchCustomerGroups()
  }, [])

  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Products`,
      link: baseUrl
    }
  ]

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSearch = () => {
    setParams(prevState => ({
      ...prevState,
      search_text: searchText,
      start_index: undefined
    }))
  }

  const handleTabChange = (_event: React.SyntheticEvent | null, newValue: string) => {
    setParams(prevState => {
      const newParams = { ...prevState, start_index: undefined }

      if (newValue) {
        newParams.filter = `productGroup Eq "${newValue}"`
      } else {
        // Remove filter if newValue is ''
        delete newParams.filter
        searchParams.delete('filter')
      }

      return newParams
    })
    searchParams.set(TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleDrawerClose = () => {
    setSelectedProductId('')
    setIsDrawerOpen(false)
  }

  const handleRowClick = (id: string) => {
    setSelectedProductId(id)
    setIsDrawerOpen(true)
  }

  const handleDelete = () => {
    if (!selectedProductId) return
    deleteProduct(selectedProductId)
      .then(unwrapResult)
      .then(() => {
        setSelectedProductId('')
        setIsDrawerOpen(false)
        fetchProductsWithParams()
        SnackbarUtils.success(t`Product successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`An error occurred while deleting the product.`)
      })
  }

  const productGroupOptions = productGroupParameterGroup?.parameters.map(
    ({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value,
      value: parameter_id
    })
  )

  // Add "All ticket types" option to the start of the array
  productGroupOptions?.unshift({ label: t`All ticket types`, value: '' })

  const isLoading = loading === 'pending' || isCustomerGroupParameterGroupLoading

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`Products`}</PageTitle>
      </Box>
      <ListActions>
        <CustomButton
          onClick={() => navigate(`${baseUrl}/create/`)}
          size="small"
          startIcon={<AddIcon />}
        >
          {t`Create new product`}
        </CustomButton>
        <ListSearch
          searchText={searchText}
          handleSearchTextChange={handleSearchTextChange}
          handleSearch={handleSearch}
          placeholder={t`Search by name or code`}
        />
      </ListActions>
      <TabContext value={tabFromParams}>
        <WithLoader loading={isProductGroupParameterGroupLoading} rows={1} height={33}>
          <TabList onChange={handleTabChange} aria-label={t`Product group tab list`}>
            {productGroupOptions?.map(({ label, value }) => (
              <Tab key={value} label={label} value={value} />
            ))}
          </TabList>
        </WithLoader>
        <Box pt={1}>
          {productGroupOptions?.map(({ value }) => (
            <TabPanel sx={{ p: 0 }} key={value} value={value}>
              <ProductList
                rows={products}
                isLoading={isLoading}
                params={params}
                handleRequestSort={handleRequestSort}
                handleChangePage={handleChangePage}
                count={total_results}
                page={
                  params.start_index
                    ? Number.parseInt(params.start_index) / ROWS_PER_PAGE_DEFAULT
                    : 0
                }
                customerGroupsParameter={customerGroupParameterGroup}
                allowSelect={false}
                selectedProductId={selectedProductId}
                onRowClick={handleRowClick}
              />
            </TabPanel>
          ))}
        </Box>
        <ProductDrawer
          open={isDrawerOpen}
          handleClose={handleDrawerClose}
          handleProductDelete={handleDelete}
          productGroupParameterGroup={productGroupParameterGroup}
          isProductGroupParameterGroupLoading={isProductGroupParameterGroupLoading}
          customerGroupParameterGroup={customerGroupParameterGroup}
          isCustomerGroupParameterGroupLoading={isCustomerGroupParameterGroupLoading}
        />
      </TabContext>
    </Box>
  )
}

export default ProductListPage
